import React, { useState, useEffect, useRef, useCallback } from 'react';
import Modal from 'react-modal';

import Loading from '../../Loading/index.js';
import ReviewStatus from '../../../config/ReviewStatus.js';
import APIUtils from '../../../utils/APIUtils';
import UnsavedChangesModal from '../../../uiComponents/UnsavedChangesModal';
import FinalConsensus from './FinalConsensus';
import ConsensusCategoryTable from './ConsensusCategoryTable';
import ConsensusLiteratureTable from './ConsensusLiteratureTable/index.js';
import ConsensusClassificationTable from './ConsensusClassificationTable';
import VariantInterpretationTags from './VariantInterpretationTags';
import CalculatedClassification from '../../AddInterpretation/CalculatedClassification/index.js';
import classificationLogic from '../../AddInterpretation/classificationLogic.js';
import { Button } from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import BlockIcon from '@mui/icons-material/Block';
import PublishIcon from '@mui/icons-material/Publish';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import MailIcon from '@mui/icons-material/Mail';

import { useNavigate, useBlocker } from 'react-router-dom';

import './index.css'

const final = "FINAL@CDC.GOV";

export default function ConsensusSummary(props) {
  const [variantInterpretations, setVariantInterpretations] = useState([]);
  const [finalVarInterp, setFinalVarInterp] = useState({});
  const [variantLiterature, setVariantLiterature] = useState([]);
  const [variantLiteratureEval, setVariantLiteratureEval] = useState([]);
  const [variantClassification, setVariantClassification] = useState([]);
  const [consensusExist, setConsensusExist] = useState(false);
  const [literatureCategory, setLiteratureCategory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);

  const [loadedReviewStatus, setLoadedReviewStatus] = useState(null);
  const [programId, setProgramId] = useState(null);
  const [variantId, setVariantId] = useState(null);

  const [modalCancelIsOpen, setModalCancelIsOpen] = useState(false);
  const [modalSubmitIsOpen, setModalSubmitIsOpen] = useState(false);
  const [modalErrorIsOpen, setModalErrorIsOpen] = useState(false);
  const [validationError, setValidationError] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');

  const [finalClassification, setFinalClassification] = useState('');
  const [finalComments, setFinalComments] = useState('');
  const [cancelAndReturn, setCancelAndReturn] = useState(false);
  const [savedFinalInterpretation, setSavedFinalInterpretation] = useState(false);
  const [savedFinalDetails, setSavedFinalDetails] = useState(false);

  const [toProgramSummary, setToProgramSummary] = useState(false);
  const [formValues, setFormValues] = useState({});

  const [calcClassification, setCalcClassification] = useState(null);
  const [selectedTagIds, setSelectedTagIds] = useState([]);
  const [savedTagIds, setSavedTagIds] = useState([]);

  const navigate = useNavigate();

  // Warn before leaving an editable consensus form with unsaved changes (browser
  // back, in-app links). The blocker fn is registered once and reads a ref so it
  // sees the latest value synchronously; intentional exits (save/publish/discard)
  // clear the ref before navigating so they're never blocked.
  const isDirtyRef = useRef(false);
  const blocker = useBlocker(
    useCallback(
      ({ currentLocation, nextLocation }) =>
        isDirtyRef.current && currentLocation.pathname !== nextLocation.pathname,
      []
    )
  );
  const handleAcceptNavigation = () => blocker.proceed?.();
  const handleCancelNavigation = () => blocker.reset?.();

  // Guard only while the consensus form is actually editable.
  useEffect(() => {
    isDirtyRef.current =
      loadedReviewStatus === ReviewStatus.READY_FOR_CONSENSUS ||
      loadedReviewStatus === ReviewStatus.CONSENSUS_IN_PROGRESS;
  }, [loadedReviewStatus]);

  // Load consensus data on mount or when variantId/programId changes
  useEffect(() => {
    if (props.variantId && props.programId) {
      setVariantId(props.variantId);
      setProgramId(props.programId);
      getConsensus(props.variantId, props.programId);
      getLiteratureCategories()
    }
  }, [props.variantId, props.programId]);

  // Handle navigation after save/discard
  useEffect(() => {
    if (cancelAndReturn || (savedFinalInterpretation && savedFinalDetails)) {
      window.onbeforeunload = () => null;
      setToProgramSummary(true);
    }
  }, [cancelAndReturn, savedFinalInterpretation, savedFinalDetails]);

  useEffect(() => {
    if (toProgramSummary) {
      navigate('/programsummary', {
        state: {
          programId: sessionStorage.getItem('program'),
        },
        replace: true
      });
    }
  }, [toProgramSummary, navigate]);

  // Notify the parent once the initial data load finishes (success or error)
  // so it can reveal the page content all at once.
  useEffect(() => {
    // If we can't load yet, don't block the parent spinner indefinitely.
    if (!props.variantId || !props.programId) {
      props.onLoaded?.();
      return;
    }

    if (!loading) {
      props.onLoaded?.();
    }
  }, [loading, props.variantId, props.programId, props.onLoaded]);

  async function getConsensus(variantId, programId) {
    setLoading(true);

    try {
    var response = await APIUtils.getConsensusSummary(programId, variantId);
    if (response) {
      var consensusSummary = response;
      var reviewStatus = consensusSummary.reviewStatus;
      var varInterps = consensusSummary.userInterpEvals;

      // A reviewer with missing or malformed interpretation details indicates a
      // data problem: on a consensus-ready variant every reviewer must have valid
      // details, and no interpretation entry should be null. Surface this to the
      // user instead of silently rendering incomplete consensus data.
      const isMalformedInterp = (interp) => {
        if (!interp) return true;
        const details = interp.interpretationDetails;
        if (details == null) return reviewStatus === ReviewStatus.READY_FOR_CONSENSUS;
        if (!Array.isArray(details)) return true;
        return details.some(detail => detail == null);
      };
      if (!Array.isArray(varInterps) || varInterps.some(isMalformedInterp)) {
        if (__ENVIRONMENT__ === 'development' || __ENVIRONMENT__ === 'test') {
          console.error('Consensus summary contains malformed reviewer interpretation data:', varInterps);
        } else {
          console.error('Consensus summary contains malformed reviewer interpretation data.');
        }
        setConsensusExist(false);
        setErrorMsg('Some consensus review data could not be loaded correctly. Please try again.');
        return;
      }

      var variantInterpretationsArr = [];
      var finalVarInterpObj = {};
      var finalUserExists = false;

      varInterps.forEach(interpretation => {
        const interpEval = {
          "userId": interpretation.userId,
          "reviewer": interpretation.reviewer,
          "sortName": interpretation.sortName
        };
        if (Array.isArray(interpretation.interpretationDetails) && interpretation.interpretationDetails.length > 0) {
          interpretation.interpretationDetails.forEach(interpretationDetail => {
            const category = interpretationDetail.AnnotationCategory;
            const catObj = interpEval[category] || {};
            catObj.evaluation = interpretationDetail.AcmgCriteria;
            catObj.comment = interpretationDetail.Comment;
            interpEval[category] = catObj;
            if (interpretation.userId === final && reviewStatus !== ReviewStatus.CLOSED) {
              finalVarInterpObj[category] = catObj.evaluation;
            }
            finalUserExists = (interpretation.userId === final);
          });
        }
        variantInterpretationsArr.push(interpEval);
      });
      if (!finalUserExists && reviewStatus === ReviewStatus.READY_FOR_CONSENSUS) {
        const interpEval = { "userId": final, "sortName": 'Final Consensus' };
        variantInterpretationsArr.push(interpEval);
      }
      // get literature categories
      const categories = await APIUtils.getLiteratureCategories();

      // format consensus literature
      var variantLiteratureArr = [];
      var variantLiteratureEvalArr =[];
      consensusSummary.userInterpEvals.forEach(review => {
        review.literatureReviews.forEach(literatureReview => {
          const catMatch = categories.find(cat => cat.literatureCategoryValue === literatureReview.literatureCategory);
          literatureReview.litCategory = catMatch ? catMatch.literatureCategoryLabel : '';
          literatureReview.litEval = literatureReview.acmgEvidCriteriaId;
          literatureReview.litComment = literatureReview.comment;
          literatureReview.finalAcmgEvidCriteriaId = reviewStatus === ReviewStatus.READY_FOR_CONSENSUS ? literatureReview.acmgEvidCriteriaId : literatureReview.finalAcmgEvidCriteriaId;
          literatureReview.reviewer = review.reviewer;
          variantLiteratureArr.push(literatureReview);
          variantLiteratureEvalArr.push(literatureReview.finalAcmgEvidCriteriaId.split(',').map(e => e.trim())); // for calculate classification logic
        });
      });

      // format consensus classification
      var variantClassificationArr = [];
      consensusSummary.userInterpEvals.forEach(review => {
        variantClassificationArr.push(review);
        if (review.userId === final) {
          setFinalClassification(review.classification);
          setFinalComments(review.comment !== "null" ? review.comment : "");
          if (review.tagIds && review.tagIds.length > 0) {
            setSavedTagIds(review.tagIds);
            setSelectedTagIds(review.tagIds);
          }
        }
      });

      setConsensusExist(true);
      setLoadedReviewStatus(reviewStatus);
      setVariantInterpretations(variantInterpretationsArr);
      setFinalVarInterp(finalVarInterpObj);
      setVariantLiterature(variantLiteratureArr);
      setVariantLiteratureEval(variantLiteratureEvalArr);
      setVariantClassification(variantClassificationArr);
 
      // Set initial form values if needed
      const isNullOrEmpty = (value) => value === null || value === undefined || value === '';
      if (
        reviewStatus === ReviewStatus.READY_FOR_CONSENSUS &&
        Object.keys(formValues).length === 0 &&
        isNullOrEmpty(finalClassification) &&
        isNullOrEmpty(finalComments)
      ) {
        loadInitialCategorySelections(varInterps, variantLiteratureEvalArr);
        loadInitialClassificationSelections(varInterps);
      } else {
        setFormValues(finalVarInterpObj);
        updateClassification(finalVarInterpObj,variantLiteratureEvalArr);
      }
    } else {
      setConsensusExist(false);
      setErrorMsg('There was an error retrieving the data.');
    }
    } catch (error) {
      if (__ENVIRONMENT__ === 'development' || __ENVIRONMENT__ === 'test') {
        console.error('Failed to load consensus summary:', error);
      } else {
        console.error('Failed to load consensus summary.');
      }
      setConsensusExist(false);
      setErrorMsg('Some consensus review data could not be loaded correctly. Please try again.');
    } finally {
      setLoading(false);
    }
  }

  async function getLiteratureCategories() {
        const response = await APIUtils.getLiteratureCategories();
        setLiteratureCategory(response);
  }
  // Find common AcmgCriteria in all arrays for each 'AnnotationCategory'

  const loadInitialCategorySelections = (varInterps, literatureEval) => {
    // Step 1: Flatten and group by 'AnnotationCategory'
    const groupedByCat = varInterps
      .flatMap(data => (Array.isArray(data?.interpretationDetails) ? data.interpretationDetails : []))
      .filter(Boolean)
      .reduce((acc, { AnnotationCategory, AcmgCriteria }) => {
        acc[AnnotationCategory] = acc[AnnotationCategory] || [];

        if (!AcmgCriteria || AcmgCriteria.length === 0) AcmgCriteria = [];

        acc[AnnotationCategory].push(AcmgCriteria);
        return acc;
      }, {});

    // Step 2: Find common AcmgCriteria in all arrays for each 'AnnotationCategory'
    const initialFormValues = Object.fromEntries(
      Object.entries(groupedByCat).map(([AnnotationCategory, criteriaLists]) => [
        AnnotationCategory,
        criteriaLists.reduce((common, list) => common.filter(value => list.includes(value)))
      ])
    );
  
    setFormValues(initialFormValues);
    updateClassification(initialFormValues,literatureEval);
  };
  
  const loadInitialClassificationSelections = (varInterps) => {
    const AllSameClassification = varInterps.every(
      obj => obj.classification === varInterps[0].classification
    );
    if (AllSameClassification) {
      setFinalClassification(varInterps[0].classification);
      setFinalComments('');
    }
  };
  const updateClassification = (categoryEval, literatureEval) => {

    let classification = classificationLogic(Object.values(categoryEval).flat(), literatureEval);
    setCalcClassification(classification);
  }
  const openCancelModal = () => setModalCancelIsOpen(true);
  const closeErrorModal = () => setModalErrorIsOpen(false);
  const openSubmitModal = () => {
    if (validateConsensus()) {
      setValidationError(false);
      setModalSubmitIsOpen(true);
    } else {
      setValidationError(true);
    }
  };
  const onDiscard = () => {
    isDirtyRef.current = false;
    setCancelAndReturn(true);
  };
  const closeModal = () => {
    setModalCancelIsOpen(false);
    setModalSubmitIsOpen(false);
  };

  const onEvaluationChange = (evalValue) => {
    setFormValues({ ...evalValue });
    updateClassification(evalValue,variantLiteratureEval)
  };

  const handleConsensusClassificationChange = (classification) => {
    setFinalClassification(classification);
  };

  const handleConsensusCommentChange = (comment) => {
    setFinalComments(comment);
  };

  const handleTagsChange = (tagIds) => {
    setSelectedTagIds(tagIds);
  };
  
  const handleFinalLitEvalChange = (updatedTableData) => {
    setVariantLiterature(updatedTableData);
    updateClassification(formValues, updatedTableData.map(row => row.finalAcmgEvidCriteriaId.split(',').map(e => e.trim())));
  };

  const save = () => {
    if (!validateConsensus()) {
      setValidationError(true);
      return;
    }
    setValidationError(false);
    saveInterpretation(true);
  };

  const submit = () => {
    saveInterpretation(false);
  };

  const validateConsensus = () => {
    if (
      (!finalClassification || finalClassification.trim() === "") ||
      (!finalComments || finalComments.trim() === "")
    ) {
      return false;
    } else {
      return true;
    }
  };

  async function saveInterpretation(partialStatus) {
    setIsSaving(true);
    const formValuesCopy = { ...formValues };
    let interpretationDetails = [];
    Object.keys(formValuesCopy).forEach((key) => {
      if (key !== undefined && formValuesCopy[key] !== '') {
        let interpElement = {
          "AnnotationCategory": key,
          "AcmgCriteria": formValuesCopy[key]
        };
        interpretationDetails.push(interpElement);
      }
    });

    let revStatus = loadedReviewStatus;
    let formData = new FormData();
    formData.append("userId", final);
    formData.append("interpretationStatus", partialStatus ? 'IN_PROGRESS' : 'COMPLETED');
    formData.append("IsReview", true);
    formData.append("calcClassification", calcClassification);
    formData.append("programClassification", finalClassification);
    formData.append("comment", finalComments);
    formData.append("interpretationDetails", JSON.stringify(interpretationDetails));
    selectedTagIds.forEach(tagId => formData.append("tagIds", tagId));
    let obj = {}; formData.forEach((value, key) => { obj[key] = value; }); console.table(obj);
    try {
      if (revStatus === ReviewStatus.READY_FOR_CONSENSUS) {
        await APIUtils.addVariantInterpretation(programId, variantId, formData);
      }
      else {
        await APIUtils.updateVariantInterpretation(programId, variantId, formData);
      }
      // Save literature reviews if any
      if (variantLiterature && variantLiterature.length > 0) {
        let finalLitEval = variantLiterature.map(row => {
          return {
            "LitReviewId": row.litReviewId,
            "FinalAcmgEvidCriteriaId": row.finalAcmgEvidCriteriaId
          };
        });
        let finalLitFormData = new FormData();
        finalLitFormData.append("literatureReviews", JSON.stringify(finalLitEval));
        await APIUtils.saveFinalConsensusLiteratureReview(finalLitFormData);
      }

      closeModal();
      // Clear the dirty guard only after every persistence call has succeeded so a
      // failed save keeps the navigation blocker armed for the still-unsaved form.
      window.onbeforeunload = null;
      isDirtyRef.current = false;
      setSavedFinalInterpretation(true);
      setSavedFinalDetails(true);
    } catch (err) {
      closeModal();
      setErrorMsg('There was an error uploading the interpretation.');
      setModalErrorIsOpen(true);
      setIsSaving(false);
    }
  }

  function getButtonSection() {
    return (
      <>
        <div className="form-buttons">
          <Button
            style={{ margin: "0 10px", width: "180px" }}
            onClick={openCancelModal}
            color="inherit"
            variant="outlined"
            sx={{
              color: '#616161',
              borderColor: '#b0b0b0',
              '&:hover': { color: 'primary.main', borderColor: 'primary.main' },
            }}
          >
            <BlockIcon className="iconMarginRight" /> Cancel
          </Button>
          <Button style={{ margin: "0 10px", width: "180px" }} onClick={save} color="primary" variant="outlined">
            <SaveIcon className="iconMarginRight" /> Save for Later
          </Button>
          <Button style={{ margin: "0 10px", width: "180px" }} onClick={openSubmitModal} color="primary" variant="contained">
            <PublishIcon className="iconMarginRight" /> Publish
          </Button>
        </div>
        <UnsavedChangesModal
          isOpen={modalCancelIsOpen}
          onConfirm={onDiscard}
          onCancel={closeModal}
        />
        <Modal
          className="modal"
          overlayClassName="modalOverlay"
          aria-modal="true"
          contentLabel="Confirm Submit"
          shouldCloseOnOverlayClick={false}
          isOpen={modalSubmitIsOpen}
          onRequestClose={closeModal}
        >
          <div className="modalContent center">
            Are you sure you want to submit this final consensus summary interpretation?
            <br />
            <br />
            <strong>Note:</strong> Hitting submit will submit this final consensus summary interpretation for others to see
            and no other edits can be made.
          </div>
          <div className="modalButtons">
            <button
              className="modalButton"
              onClick={closeModal}
            >
              Cancel
            </button>
            <button className="modalButton primaryButton" onClick={submit}>
              Yes, Submit
            </button>
          </div>
        </Modal>
        <Modal
          className="modal"
          overlayClassName="modalOverlay"
          aria-modal="true"
          contentLabel="Error"
          shouldCloseOnOverlayClick={false}
          isOpen={modalErrorIsOpen}
          onRequestClose={closeErrorModal}
        >
          <div className="modalContent flexContent">
            <span className="requiredIndicator">
              <ErrorOutlineIcon fontSize="large" className="validationWarning" />
            </span>
            <div className="textContent">
              {errorMsg}
            </div>
          </div>
          <div className="modalButtons">
            <button
              className="modalButton primaryButton"
              onClick={closeErrorModal}
            >
              OK
            </button>
          </div>
        </Modal>
      </>
    );
  }

  const showSaveButtons = loadedReviewStatus === ReviewStatus.READY_FOR_CONSENSUS || loadedReviewStatus === ReviewStatus.CONSENSUS_IN_PROGRESS;

  return (
    <>
      <Loading isLoading={loading} />
      {isSaving && (
        <div className="modalOverlay">
          <Loading isLoading={true} />
        </div>
      )}
      <UnsavedChangesModal
        isOpen={blocker && blocker.state === "blocked"}
        onConfirm={handleAcceptNavigation}
        onCancel={handleCancelNavigation}
      />
      {loading ? '' :
        consensusExist ? (
          <>
            <ConsensusCategoryTable
              data={variantInterpretations}
              reviewStatus={loadedReviewStatus}
              formValues={formValues}
              finalVarInterp={finalVarInterp}
              onEvaluationChange={onEvaluationChange}
            />
            <ConsensusLiteratureTable data={variantLiterature} 
              handleFinalLitEvalChange={handleFinalLitEvalChange}  
              literatureCategory={literatureCategory}
              reviewStatus={loadedReviewStatus} 
            />
            <ConsensusClassificationTable data={variantClassification} />

            {loadedReviewStatus !== ReviewStatus.CLOSED ? (
              <>
              <VariantInterpretationTags
                savedTagIds={savedTagIds}
                onTagsChange={handleTagsChange}
              />
              <CalculatedClassification calculatedClassification={calcClassification} />
              <FinalConsensus
                finalClassification={finalClassification}
                finalComments={finalComments}
                handleConsensusClassificationChange={handleConsensusClassificationChange}
                handleConsensusCommentChange={handleConsensusCommentChange} />
              </>
            ) : (
              <VariantInterpretationTags
                savedTagIds={savedTagIds}
                readOnly
              />
            )}
            {validationError ? (
              <div className="error validationError" align="center">Both Classification and Comments are required.</div>
            ) : ('')}
            <div className='buttonSection'>
              {showSaveButtons && (
                getButtonSection()
              )}
            </div>
          </>
        ) : errorMsg ? (
          <>
            <div className='errorMsg'>
              <ErrorOutlineIcon fontSize='small' /> {errorMsg} If the problem persists, please contact&nbsp;<a href="mailto:cdced3nsupport@cdc.gov?Subject=ED3N%20Consensus%20Review%20Data%20Problem"><MailIcon style={{ verticalAlign: 'middle', fontSize: '1.3rem', marginTop: '-2px', marginRight: '1px' }} />ED3N support</a>.
            </div>
            <div style={{ minHeight: '400px' }}></div>
          </>
        ) : (
          <div className="noData">
            There is no consensus for this variant yet
          </div>
        )}
    </>
  );
}